import * as React from 'react'
import { Search } from 'lucide-react'
import { Button } from '../../../components/ui/button'
import { Checkbox } from '../../../components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '../../../components/ui/dialog'
import {
  clampPage,
  counterpartNames,
  LOOKUP_PAGE_SIZE,
  pageCount,
  pageSlice,
  type MappingSide,
  type TagLookupSource
} from './tagMappingPreviewView'

export interface TagLinkSource {
  side: MappingSide
  name: string
}

export function TagMappingLinkDialog({
  source,
  preview,
  sideLabels,
  alreadyLinked,
  onClose,
  onConfirm
}: {
  source: TagLinkSource | null
  preview: TagLookupSource
  sideLabels: Record<MappingSide, string>
  alreadyLinked: ReadonlySet<string>
  onClose: () => void
  onConfirm: (counterparts: string[]) => void
}) {
  const [query, setQuery] = React.useState('')
  const [page, setPage] = React.useState(0)
  const [selected, setSelected] = React.useState<string[]>([])

  React.useEffect(() => {
    setQuery('')
    setPage(0)
    setSelected([])
  }, [source?.side, source?.name])

  const counterpartSide = source?.side === 'a' ? sideLabels.b : sideLabels.a
  const names = source ? counterpartNames(preview, source.side, query) : []
  const tooShort = query.trim().length > 0 && query.trim().length < 2
  const safePage = clampPage(page, names.length)
  const pages = pageCount(names.length)
  const visible = pageSlice(names, safePage)

  const toggle = (name: string) => {
    if (alreadyLinked.has(name.trim().toUpperCase())) return
    setSelected((current) =>
      current.some((entry) => entry.toUpperCase() === name.toUpperCase())
        ? current.filter((entry) => entry.toUpperCase() !== name.toUpperCase())
        : [...current, name]
    )
  }

  return (
    <Dialog open={source != null} onOpenChange={(open) => (!open ? onClose() : undefined)}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Link {source?.name ?? 'tag'}</DialogTitle>
          <DialogDescription>
            Search {counterpartSide} tags and select one or more counterparts.
          </DialogDescription>
        </DialogHeader>
        <label className="flex flex-col gap-1.5">
          <span className="text-10 font-medium tracking-[0.06em] text-ink-3 uppercase">
            Find {counterpartSide} tags
          </span>
          <span className="relative">
            <Search className="pointer-events-none absolute top-1/2 left-2 size-3.5 -translate-y-1/2 text-ink-3" />
            <input
              type="search"
              value={query}
              onChange={(event) => {
                setQuery(event.target.value)
                setPage(0)
              }}
              placeholder={`${counterpartSide} name`}
              className="h-8 w-full rounded-md border border-line-2 bg-bg-sunk pr-2 pl-7 font-mono text-12 text-ink outline-none placeholder:font-sans placeholder:text-ink-3 focus:border-brand"
            />
          </span>
        </label>
        {tooShort ? (
          <p className="text-11 text-ink-3">Type at least two characters.</p>
        ) : names.length === 0 ? (
          <p className="text-11 text-ink-3">No {counterpartSide} tags match.</p>
        ) : (
          <div className="flex max-h-72 flex-col overflow-hidden rounded-md border border-line-2">
            <ul className="flex-1 overflow-auto">
              {visible.map((name) => {
                const linked = alreadyLinked.has(name.trim().toUpperCase())
                const checked =
                  linked || selected.some((entry) => entry.toUpperCase() === name.toUpperCase())
                return (
                  <li key={name} className="border-b border-line-2 last:border-b-0">
                    <label className="flex cursor-pointer items-center gap-2 px-2.5 py-1.5">
                      <Checkbox
                        checked={checked}
                        disabled={linked}
                        onCheckedChange={() => toggle(name)}
                        aria-label={`Select ${name}`}
                      />
                      <span className="min-w-0 flex-1 font-mono text-12 text-ink">{name}</span>
                      {linked ? <span className="text-10 text-ink-3">Linked</span> : null}
                    </label>
                  </li>
                )
              })}
            </ul>
            {pages > 1 ? (
              <div className="flex items-center justify-between gap-2 border-t border-line-2 px-2.5 py-1.5">
                <p className="text-10 text-ink-3">
                  {safePage * LOOKUP_PAGE_SIZE + 1}–
                  {Math.min(names.length, safePage * LOOKUP_PAGE_SIZE + LOOKUP_PAGE_SIZE)} of{' '}
                  {names.length}
                </p>
                <div className="flex items-center gap-1">
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    disabled={safePage <= 0}
                    onClick={() => setPage(safePage - 1)}
                  >
                    Previous
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    disabled={safePage >= pages - 1}
                    onClick={() => setPage(safePage + 1)}
                  >
                    Next
                  </Button>
                </div>
              </div>
            ) : null}
          </div>
        )}
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="button"
            disabled={selected.length === 0}
            onClick={() => onConfirm(selected)}
          >
            Link {selected.length || ''}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
